"use client";

import { useEffect, useState } from "react";
import CustomButton from "./CustomButton";
import AnimateOnScroll from "./AnimateOnScroll";
import Image from "next/image";
import Quote from "../icons/Quote";
import { Star } from "lucide-react";
import {
  Marquee,
  MarqueeContent,
  MarqueeItem,
} from "@/components/kibo-ui/marquee";
import { SITE_URL, ORGANIZATION_NAME } from "@/lib/metadata";
import {
  GoogleReview,
  GoogleReviewsData,
  GOOGLE_MAPS_PLACE_URL,
} from "@/lib/googleReviews";
import CallExpandBtn from "./CallExpandBtn";

const ReviewCardSkeleton = () => (
  <div
    className="relative w-[82vw] shrink-0 overflow-hidden rounded-[7.95px] bg-black/[0.04] lg:w-[440px] 3xl:w-[592px]"
    aria-hidden
  >
    <div className="flex h-[400px] animate-pulse flex-col justify-between p-5 lg:p-[31.8px] 3xl:h-[507px]">
      <div className="flex flex-col gap-3 lg:gap-4">
        <div className="size-6 rounded bg-black/10" />
        <div className="flex flex-col gap-2">
          <div className="h-4 w-full rounded bg-black/10" />
          <div className="h-4 w-full rounded bg-black/10" />
          <div className="h-4 w-3/4 rounded bg-black/10" />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="h-4 w-32 rounded bg-black/10" />
        <div className="h-3 w-20 rounded bg-black/10" />
      </div>
    </div>
  </div>
);

const GoogleReviewsSkeleton = () => (
  <section className="bg-white" aria-hidden aria-label="Loading Google reviews">
    <div className="container mb-10 flex flex-col gap-6 px-5 lg:flex-row lg:items-start lg:justify-between">
      <div className="flex animate-pulse flex-col gap-3">
        <div className="h-6 w-32 rounded bg-black/10" />
        <div className="h-9 w-[400px] max-w-full rounded bg-black/10" />
      </div>
      <div className="flex w-full animate-pulse flex-col gap-5 lg:w-[575px] lg:shrink-0">
        <div className="h-5 w-full rounded bg-black/10" />
        <div className="h-11 w-48 rounded-xl bg-black/10" />
      </div>
    </div>
    <div className="container flex gap-[10px] overflow-hidden px-5">
      {Array.from({ length: 3 }).map((_, i) => (
        <ReviewCardSkeleton key={i} />
      ))}
    </div>
  </section>
);

interface ReviewCardProps {
  review: GoogleReview;
}

const ReviewCard = ({ review }: ReviewCardProps) => (
  <article
    className="relative w-[82vw] shrink-0 overflow-hidden rounded-[7.95px] lg:w-[440px] 3xl:w-[592px]"
    aria-label={`Google review from ${review.author}`}
  >
    {/* Background image — fills the card */}
    <Image
      src="/images/v3/home/testimonial-bg.png"
      alt=""
      fill
      className="object-cover"
      sizes="(max-width: 640px) 82vw, (max-width: 1024px) 440px, 592px"
      aria-hidden
    />

    {/* Content sits on top of the bg image */}
    <div className="relative z-10 flex h-[400px] flex-col justify-between p-5 lg:p-[31.8px] 3xl:h-[507px]">
      {/* Quote icon + review text */}
      <div className="flex flex-col gap-3 lg:gap-4">
        <Quote />
        <p className="line-clamp-[9] font-onest text-base font-medium leading-[130%] text-v3-textV2 lg:text-lg 3xl:text-[28px]">
          {review.text}
        </p>
      </div>

      {/* Author info */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-crimsonText text-[16px] font-bold text-v3-textV2 lg:text-[18px]">
            {review.author}
          </span>
          <div className="flex items-center gap-[2px]" aria-hidden>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                size={13}
                className={
                  i < review.rating
                    ? "fill-[#FFC933] text-[#FFC933]"
                    : "fill-transparent text-[#D1D1D1]"
                }
              />
            ))}
          </div>
        </div>
        <span className="font-onest text-[13px] font-normal text-[#D1D1D1] lg:text-[14px]">
          {review.relativeTime}
        </span>
      </div>
    </div>
  </article>
);

const buildSchema = (data: GoogleReviewsData) => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  name: ORGANIZATION_NAME,
  url: SITE_URL,
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: data.rating,
    reviewCount: data.userRatingsTotal,
    bestRating: "5",
  },
  review: data.reviews.map((r) => ({
    "@type": "Review",
    author: { "@type": "Person", name: r.author },
    reviewBody: r.text,
    reviewRating: {
      "@type": "Rating",
      ratingValue: r.rating,
      bestRating: "5",
    },
    datePublished: new Date(r.time * 1000).toISOString(),
  })),
});

interface GoogleReviewsProps {
  fallback?: React.ReactNode;
}

const GoogleReviews = ({ fallback = null }: GoogleReviewsProps) => {
  const [data, setData] = useState<GoogleReviewsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch("/api/google-reviews")
      .then((res) => {
        if (!res.ok)
          throw new Error(`Request failed with status ${res.status}`);
        return res.json();
      })
      .then((json) => {
        if (json?.reviews?.length > 0) setData(json);
        else setFailed(true);
      })
      .catch((err) => {
        console.error("Error loading Google reviews:", err);
        setFailed(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <GoogleReviewsSkeleton />;
  if (failed || !data) return <>{fallback}</>;

  return (
    <section className="bg-white" aria-labelledby="google-reviews-heading">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(buildSchema(data)).replace(/</g, "\\u003c"),
        }}
      />
      {/* ── Header ── */}
      <div className="container mb-10 flex flex-col gap-6 px-5 lg:flex-row lg:items-start lg:justify-between">
        {/* Left: subtitle + title */}
        <AnimateOnScroll className="flex flex-col gap-3">
          <span
            data-from-left
            className="font-crimsonText text-[20px] font-bold text-v3-highlight lg:text-[28px]"
          >
            Google Reviews
          </span>
          <h2
            id="google-reviews-heading"
            className="heading max-w-[641px] text-v3-secondary"
          >
            Verified by {data.userRatingsTotal}+ Google Reviews
          </h2>
        </AnimateOnScroll>

        {/* Right: description + CTA */}
        <div className="flex w-full flex-col gap-5 lg:w-[575px] lg:shrink-0">
          <p className="font-onest text-[16px] font-normal leading-[110%] text-v3-body lg:text-[20px]">
            Real experiences from people who found clarity, confidence, and
            mental health support through professional counselling, coaching,
            and guided therapy.
          </p>
          <div className="flex w-fit flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <CallExpandBtn />
            <CustomButton
              label="See All Reviews on Google"
              href={GOOGLE_MAPS_PLACE_URL}
              target="_blank"
              variant="primary"
              withIcon
              size="lg"
            />
          </div>
        </div>
      </div>

      {/* ── Marquee ── */}
      <Marquee aria-label="Google Reviews">
        <MarqueeContent speed={40} pauseOnHover>
          {data.reviews.map((r, i) => (
            <MarqueeItem key={i} className="mx-[10px]">
              <ReviewCard review={r} />
            </MarqueeItem>
          ))}
        </MarqueeContent>
      </Marquee>
    </section>
  );
};

export default GoogleReviews;
